import { useState, useEffect, useRef } from 'react';
import { Video, Square, X, Loader } from 'lucide-react';
import { MAX_VIDEO_DURATION, VIDEO_CONSTRAINTS, SUPPORTED_MIME_TYPES } from '../../utils/constants';
import { formatDuration } from '../../utils/formatters';

function RecordingScreen({ event, guestInfo, onComplete, onCancel }) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [error, setError] = useState(null);
  const [isInitializing, setIsInitializing] = useState(false);

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  // Initialize camera on mount
  useEffect(() => {
    initializeCamera();

    return () => {
      cleanup();
    };
  }, []);

  const initializeCamera = async () => {
    try {
      setIsInitializing(true);
      setError(null);

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
      streamRef.current = stream;

      // Display preview
      if (videoRef.current) {
        videoRef.current.srcObject = stream;

        // Ensure video plays immediately (don't use load() with srcObject!)
        try {
          await videoRef.current.play();
        } catch (playError) {
          console.warn('Autoplay failed, will retry:', playError);
          // Safari sometimes needs a delay and multiple retries
          setTimeout(async () => {
            try {
              if (videoRef.current) {
                await videoRef.current.play();
              }
            } catch (retryError) {
              console.error('Retry play failed:', retryError);
              // Last resort: try one more time after another delay
              setTimeout(() => {
                videoRef.current?.play().catch(e => console.error('Final retry failed:', e));
              }, 200);
            }
          }, 100);
        }
      }
    } catch (err) {
      console.error('Camera initialization error:', err);
      setError('Unable to access camera. Please check permissions.');
    }
  };

  const getSupportedMimeType = () => {
    for (const mimeType of SUPPORTED_MIME_TYPES) {
      if (MediaRecorder.isTypeSupported(mimeType)) {
        return mimeType;
      }
    }
    return SUPPORTED_MIME_TYPES[0]; // Fallback to first option
  };

  const startRecording = () => {
    if (!streamRef.current) {
      setError('Camera not initialized');
      return;
    }

    try {
      chunksRef.current = [];
      const mimeType = getSupportedMimeType();

      const mediaRecorder = new MediaRecorder(streamRef.current, {
        mimeType: mimeType,
        videoBitsPerSecond: 2500000 // 2.5 Mbps
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        onComplete(blob);
      };

      mediaRecorder.start(100); // Collect data every 100ms
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime((prev) => {
          const newTime = prev + 1;
          if (newTime >= MAX_VIDEO_DURATION) {
            stopRecording();
          }
          return newTime;
        });
      }, 1000);
    } catch (err) {
      console.error('Recording start error:', err);
      setError('Failed to start recording');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const cleanup = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }
  };

  const handleCancel = () => {
    cleanup();
    onCancel();
  };

  const timeRemaining = MAX_VIDEO_DURATION - recordingTime;
  const progressPercentage = (recordingTime / MAX_VIDEO_DURATION) * 100;

  return (
    <div className="fullscreen flex flex-col bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 p-6 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">
              {isRecording ? 'Recording...' : 'Ready to Record'}
            </h2>
            <p className="text-neutral-grey-300">
              {guestInfo.name} • {event.eventName}
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-all"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>
      </div>

      {/* Video Preview */}
      <div className="flex-1 relative z-0">
        <video
          ref={videoRef}
          autoPlay
          muted
          playsInline
          webkit-playsinline="true"
          x-webkit-airplay="allow"
          className="absolute inset-0 w-full h-full object-cover transform scale-x-[-1] z-0"
        />

        {/* Error Overlay */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-20">
            <div className="text-center">
              <div className="w-20 h-20 bg-crimson/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="w-10 h-10 text-crimson" />
              </div>
              <p className="text-crimson text-lg mb-4">{error}</p>
              <button
                onClick={initializeCamera}
                className="px-6 py-3 bg-gold hover:bg-gold-light text-dark-slate font-semibold rounded-lg transition-all"
              >
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* MomentBox Watermark - Always visible */}
        <div className="absolute bottom-6 right-6 z-10 opacity-60">
          <div className="flex items-center gap-2 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-lg border border-white/10">
            <Video className="w-4 h-4 text-gold" />
            <span className="text-white text-sm font-semibold tracking-wide">MomentBox</span>
          </div>
        </div>

        {/* Recording Indicator with Progress */}
        {isRecording && (
          <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10">
            <div className="relative">
              {/* Circular Progress Ring */}
              <svg className="w-20 h-20 -rotate-90" viewBox="0 0 80 80">
                {/* Background circle */}
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  fill="none"
                  stroke="rgba(220, 38, 38, 0.2)"
                  strokeWidth="3"
                />
                {/* Progress circle */}
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  fill="none"
                  stroke="#DC2626"
                  strokeWidth="3"
                  strokeDasharray={`${2 * Math.PI * 36}`}
                  strokeDashoffset={`${2 * Math.PI * 36 * (1 - progressPercentage / 100)}`}
                  className="transition-all duration-300"
                  strokeLinecap="round"
                />
              </svg>
              {/* REC Badge with Timer */}
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="flex items-center gap-1 mb-0.5">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  <span className="text-white font-bold text-xs">REC</span>
                </div>
                <span className="text-white font-semibold text-xs">{formatDuration(recordingTime)}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20 p-8 bg-gradient-to-t from-black/80 to-transparent">

        {/* Record Button */}
        <div className="flex justify-center">
          {!isRecording ? (
            <button
              onClick={startRecording}
              disabled={!!error}
              className="w-24 h-24 bg-crimson hover:bg-crimson/90 rounded-full flex items-center justify-center transition-all shadow-2xl shadow-crimson/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Video className="w-12 h-12 text-white" />
            </button>
          ) : (
            <button
              onClick={stopRecording}
              className="w-24 h-24 bg-gold hover:bg-gold-light rounded-full flex items-center justify-center transition-all shadow-2xl shadow-gold/50"
            >
              <Square className="w-10 h-10 text-dark-slate" fill="currentColor" />
            </button>
          )}
        </div>

        <p className="text-center text-neutral-grey-300 mt-6 text-lg">
          {!isRecording
            ? 'Tap the red button to start recording'
            : 'Tap the gold button to stop'}
        </p>
      </div>
    </div>
  );
}

export default RecordingScreen;
